import {
	ChangeDetectorRef,
	Component,
	ContentChild,
	ElementRef,
	EventEmitter,
	forwardRef,
	Input,
	Output,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorBase } from '@shared/bases/control-value-accessor.base';
import { SharedModule } from '@shared/modules/shared.module';
import {
	formatDecimalInput,
	isEditableDecimalInput,
	normalizeDecimalInput,
	parseDecimalInput,
	stepDecimalInput,
} from '@shared/utils/decimal-input';

@Component({
	selector: 'app-input',
	imports: [SharedModule],
	templateUrl: './input.component.html',
	styleUrl: './input.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => InputComponent),
			multi: true,
		},
	],
})
export class InputComponent extends ControlValueAccessorBase<string | number | null> {
	@Input() placeholder = '';
	@Input() size: 'large' | 'medium' | 'small' | 'xsmall' = 'medium';
	@Input() disabled = false;
	@Input() error = false;
	@Input() errorMessage = '';
	@Input() icon = '';
	@Input() iconPosition: 'left' | 'right' = 'left';
	@Input() label?: string;
	@Input() helperText?: string;
	@Input() inputId = `input-${Math.random().toString(36).substring(2, 9)}`;
	@Input() type: 'text' | 'email' | 'tel' | 'password' | 'number' | 'decimal' = 'text';
	/** `step` for numeric inputs; use 'any' when no fixed increment applies. */
	@Input() step?: string | number;
	// Optional passthrough for numeric inputs (e.g. min="0" to block negatives).
	@Input() min?: string | number;
	@Input() customInputHandler?: (event: Event) => void;

	@Output() blurred = new EventEmitter<void>();
	@Output() enterPressed = new EventEmitter<void>();
	@Output() focused = new EventEmitter<void>();

	@ContentChild('suffix') suffix: TemplateRef<any> | null = null;

	@ViewChild('inputRef') inputRef!: ElementRef<HTMLInputElement>;

	displayValue = '';

	constructor(private cdRef: ChangeDetectorRef) {
		super();
	}

	/** Safari handles localized decimals inconsistently in native number inputs. */
	get nativeType(): 'text' | 'email' | 'tel' | 'password' | 'number' {
		return this.type === 'decimal' ? 'text' : this.type;
	}

	get nativeInputMode(): 'decimal' | null {
		return this.type === 'decimal' ? 'decimal' : null;
	}

	get decimalPattern(): string | null {
		return this.type === 'decimal' ? '-?[0-9]*([.,][0-9]*)?' : null;
	}

	get errorId(): string {
		return `${this.inputId}-error`;
	}

	get helperId(): string {
		return `${this.inputId}-helper`;
	}

	get describedBy(): string | null {
		const ids = [
			this.error && this.errorMessage ? this.errorId : null,
			this.helperText ? this.helperId : null,
		].filter((id): id is string => id !== null);
		return ids.length > 0 ? ids.join(' ') : null;
	}

	/**
	 * Callback triggered on native input change.
	 * Updates the internal value and notifies Angular.
	 */
	onInput(event: Event): void {
		const input = event.target as HTMLInputElement;

		// If there is a custom handler, run it first
		if (this.customInputHandler) {
			this.customInputHandler(event);
		}

		if (this.type === 'decimal') {
			if (!isEditableDecimalInput(input.value)) {
				input.value = this.displayValue;
				return;
			}

			// Keep the separator chosen by the user visible, but expose a canonical
			// dot-decimal value to Angular and every API payload builder.
			this.displayValue = input.value;
			const normalizedValue = normalizeDecimalInput(input.value);
			this.value = parseDecimalInput(normalizedValue) === null ? '' : normalizedValue;
			return;
		}

		this.displayValue = input.value;
		this.value = input.value;
	}

	/**
	 * Allows Angular to set the disabled state of the input.
	 */
	override setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	/**
	 * Handles blur event and emits it
	 */
	onBlur(): void {
		this.markAsTouched();
		this.blurred.emit();
	}

	/**
	 * Handles focus event and emits it
	 */
	onFocus(): void {
		this.focused.emit();
	}

	/**
	 * Handles keydown event
	 */
	onKeyDown(event: KeyboardEvent): void {
		if (this.type === 'decimal' && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
			event.preventDefault();
			this.stepDecimal(event.key === 'ArrowUp' ? 1 : -1);
			return;
		}
		if (event.key === 'Enter') {
			event.preventDefault(); // Prevent form submission
			this.enterPressed.emit();
		}
	}

	/**
	 * Ensures the DOM input reflects the latest value written from Angular.
	 */
	override writeValue(value: string | number | null): void {
		const modelValue = this.type === 'decimal' && typeof value === 'string' ? normalizeDecimalInput(value) : value;
		super.writeValue(modelValue);
		this.displayValue = this.type === 'decimal' ? formatDecimalInput(value) : String(value ?? '');
		this.cdRef.markForCheck(); // forces re-evaluation if using OnPush
		if (this.inputRef) {
			this.inputRef.nativeElement.value = this.displayValue;
		}
	}

	stepDecimal(direction: 1 | -1): void {
		if (this.type !== 'decimal' || this.disabled || this.step === 'any') return;

		const numericStep = this.step === undefined ? 1 : Number(this.step);
		if (!Number.isFinite(numericStep) || numericStep <= 0) return;

		const current = parseDecimalInput(this.value) ?? 0;
		let next = stepDecimalInput(current, numericStep, direction);
		const numericMin = this.min === undefined ? null : Number(this.min);
		if (numericMin !== null && Number.isFinite(numericMin)) next = Math.max(next, numericMin);

		const canonicalValue = String(next);
		this.displayValue = formatDecimalInput(canonicalValue);
		this.value = canonicalValue;
		if (this.inputRef) {
			this.inputRef.nativeElement.value = this.displayValue;
			this.inputRef.nativeElement.focus();
		}
	}
}
